import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import { FC, createContext, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { setModal } from 'modules/_shared/redux/modalActions';
import SendTestEmail from 'modules/_shared/components/Modals/SendTestEmail';
import { SendTestEmailModalProvider } from 'modules/_shared/components/Modals/SendTestEmail/context/SendTestEmailModalContext';
import { notifyDividendToPartners } from 'modules/communications/api/notifyPartnersDividend';
import { addConfigurationToEmailData } from 'utils/emails';
import { useGetPartnersData } from '../hooks/useGetPartnersData';

import { notifyPartnersDividendsModalInitValue } from '../constants/notifyPartnersDividendsModalInitValue';

import { NotifyPartnersDividendsModalContextType } from '../types/NotifyPartnersDividendsModalContextType';
import { PartnerData } from '../types/PartnerData';

import { getEmailData } from '../utils/getEmailData';

const NotifyPartnersDividendsModalContext =
  createContext<NotifyPartnersDividendsModalContextType>(
    notifyPartnersDividendsModalInitValue
  );

export const NotifyPartnersDividendsModalProvider: FC<{
  children: React.ReactNode; // eslint-disable-line
  operationId: string | undefined;
}> = ({ children, operationId }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const [expandView, setExpandView] = useState<boolean>(false);
  const [expandDirection, setExpandDirection] = useState<string>('down');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { data: dividendPartnersData, isLoading: isLoadingPartners } =
    useGetPartnersData({
      operationId,
      page: currentPage,
    });

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeView = () => {
    const newView = !expandView;
    const newDirection = newView ? 'up' : 'down';
    setExpandView(newView);
    setExpandDirection(newDirection);
  };

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
  };

  const handleChangeRowsPerPage = (rowsPerPage: number) => {
    setRowsPerPage(rowsPerPage);
  };

  const handleSendEmails = async () => {
    try {
      setIsLoading(true);
      if (!operationId) {
        return;
      }
      const emailData = getEmailData({
        subject: `${t('PartnersDividends')}-${actualSociety?.name}`,
        actualSociety,
        recipients: [],
        userId: user?._id,
      });

      const emailDataWithConfiguration = actualSociety
        ? addConfigurationToEmailData(actualSociety, emailData)
        : emailData;

      await store.dispatch(
        notifyDividendToPartners({
          operationId,
          societyId: actualSociety?._id,
          emailData: emailDataWithConfiguration,
        })
      );

      successSwal(t('NotificationSentSuccessfully'));
      handleCloseModal();
    } catch (error) {
      errorSwal(t('ErrorSendingNotification'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    const recipients: PartnerData[] = [];

    const emailData = getEmailData({
      subject: `${t('PartnersDividends')}-${actualSociety?.name}`,
      actualSociety,
      recipients,
      userId: user?._id,
    });

    handleCloseModal();

    dispatch(
      setModal(
        <SendTestEmailModalProvider emailData={emailData}>
          <SendTestEmail />
        </SendTestEmailModalProvider>
      )
    );
  };

  return (
    <NotifyPartnersDividendsModalContext.Provider
      value={{
        data: {
          partnersData: dividendPartnersData.partnersData,
          totalPartners: dividendPartnersData.totalPartners,
          expandView,
          expandDirection,
          invalidEmailsAlert: dividendPartnersData.invalidEmailsAlert,
          hasInvalidEmails: dividendPartnersData.hasInvalidEmails,
          hasGeneratedEmails: dividendPartnersData.hasGeneratedEmails,
          generatedEmailsAlert: dividendPartnersData.generatedEmailsAlert,
          currentPage,
          rowsPerPage,
          isLoading: isLoading || isLoadingPartners,
        },
        actions: {
          handleChangeView,
          handleCloseModal,
          handleSendEmails,
          handleSendTestEmail,
          handleChangePage,
          handleChangeRowsPerPage,
        },
      }}
    >
      {children}
    </NotifyPartnersDividendsModalContext.Provider>
  );
};

export const useNotifyPartnersDividendsModal = () =>
  useContext(NotifyPartnersDividendsModalContext);
